// Pure helpers of the agent drawer (no DOM): status line, settings form state, diffing and
// validation, connector rows, lessons, server problems, chat payload normalisation.
// Unit-tested in test/ui-agent-model.test.js.
import { t } from '../../i18n/fr.js';
import { connectorLabel } from '../../format.js';
import { matchConnector, isLibraryConnector } from '../connectorlines.js';
import './strings.js';

export const ENGINES = ['claude', 'codex', 'openai-image', 'gemini', 'kie'];
/** Engines that take an effort (Claude's effort, Gemini's thinking level). */
export const EFFORT_ENGINES = ['claude', 'gemini'];
/** Engines without any connector (they analyse what they are given). */
export const NO_CONNECTOR_ENGINES = ['openai-image', 'gemini', 'kie'];
export const EFFORTS = ['low', 'medium', 'high', 'xhigh', 'max'];
export const STATUSES = ['idle', 'working', 'waiting', 'error', 'done'];
/** Drawer tabs in display order (route #/agent/<id>/<tab>); 'overview' is the default. */
export const TABS = ['overview', 'routines', 'tasks', 'chat', 'settings'];

/** Size limits of the editable fields (SPEC §13). */
export const LIMITS = Object.freeze({ name: 22, role: 80, does: 300, brief: 20000, model: 80, lesson: 300, lessons: 30, chat: 4000, timeoutMin: 5, timeoutMax: 180 });

/** Characters the server accepts in a model name (same rule as server/office/validate.js). */
export const MODEL_RE = /^[A-Za-z0-9._:/@[\]-]*$/;

/** Model names suggested per engine (free text is still accepted; empty = the engine's default). */
export const MODEL_SUGGESTIONS = Object.freeze({
  claude: ['sonnet', 'opus', 'haiku', 'fable'],
  codex: ['gpt-6-sol', 'gpt-6-astra', 'gpt-6-luna'],
  'openai-image': ['gpt-image-2'],
  gemini: ['gemini-3.8-flash'],
  // Kie.ai: the model ids of its API (Seedance 2.0 by default; the others are for ads A/B tests,
  // motion transfer, a talking avatar, an upscale, music with Suno V6 — see docs/ENGINES.md
  // « Kie.ai (vidéo) »).
  kie: [
    'bytedance/seedance-2', 'bytedance/seedance-2-fast', 'bytedance/seedance-2-mini', 'bytedance/seedance-2-5',
    'minimax-h3/image-to-video', 'minimax-h3/reference-to-video', 'kling-3.0/motion-control',
    'wan/2-2-animate-move', 'wan/2-2-animate-replace', 'wan/2-7-r2v', 'wan/2-7-videoedit',
    'infinitalk/from-audio', 'topaz/video-upscale', 'google/veo-3.1-fast', 'google/veo-3.1-lite', 'google/veo-3.1',
    'suno/v6',
  ],
});

/**
 * Short French names of suggested models whose id says little (the chip shows the name, its
 * tooltip the id). Same labels as KIE_MODELS in server/engines/kie.js (checked by the tests).
 */
export const MODEL_LABELS = Object.freeze({
  'bytedance/seedance-2': 'Seedance 2.0',
  'bytedance/seedance-2-fast': 'Seedance 2.0 Fast (moins cher)',
  'bytedance/seedance-2-mini': 'Seedance 2.0 Mini (brouillons)',
  'bytedance/seedance-2-5': 'Seedance 2.5 (jusqu’à 30 s)',
  'minimax-h3/image-to-video': 'MiniMax H3 (image de départ)',
  'minimax-h3/reference-to-video': 'MiniMax H3 (références)',
  'kling-3.0/motion-control': 'Kling 3.0 (transfert de mouvement)',
  'wan/2-2-animate-move': 'Wan Animate (mouvement)',
  'wan/2-2-animate-replace': 'Wan Animate (remplacement)',
  'wan/2-7-r2v': 'Wan 2.7 (références)',
  'wan/2-7-videoedit': 'Wan 2.7 (retouche vidéo)',
  'infinitalk/from-audio': 'InfiniTalk (avatar qui parle)',
  'topaz/video-upscale': 'Topaz (agrandir une vidéo)',
  'google/veo-3.1-fast': 'Veo 3.1 Fast (voix intégrée)',
  'google/veo-3.1-lite': 'Veo 3.1 Lite (le moins cher)',
  'google/veo-3.1': 'Veo 3.1 Quality',
  'suno/v6': 'Suno V6 (musique)',
});

/** The short French name of a model id, or '' (the id speaks for itself). */
export function modelLabel(model) {
  return Object.hasOwn(MODEL_LABELS, model) ? MODEL_LABELS[model] : '';
}

/** Placeholder of the model field per engine (the default model when it is worth naming). */
export function modelPlaceholderKey(engine) {
  return engine === 'kie' ? 'ad.f.modelPlaceholderKie' : 'ad.f.modelPlaceholder';
}

/**
 * French display name of an engine (SPEC §9 names): Claude, Codex (ChatGPT), Images OpenAI, Gemini,
 * Kie.ai (vidéo).
 * @param {string} [engine] empty → Claude (the default engine)
 */
export function engineLabel(engine) {
  const key = ENGINES.includes(engine) ? engine : 'claude';
  return t(`engine.name.${key}`);
}

/** Suggested models of an engine. */
export function modelSuggestions(engine) {
  return MODEL_SUGGESTIONS[engine] ? [...MODEL_SUGGESTIONS[engine]] : [];
}

/** Code-point length (an accented letter counts as one). */
export function charLength(s) {
  return [...String(s ?? '')].length;
}

/** The desk label: upper case, single spaces (same rule as the server). */
export function normalizeName(v) {
  return String(v ?? '').replace(/\s+/g, ' ').trim().toLocaleUpperCase('fr-FR');
}

/**
 * The initial tab of the drawer: the requested one when allowed, else the overview (Aperçu). Old
 * links (#/agent/<id>/chat, …/settings) keep working.
 * @param {string|null|undefined} tab
 * @param {{canManage?: boolean}} [perms]
 */
export function resolveTab(tab, { canManage = false } = {}) {
  if (tab === 'settings' && !canManage) return 'overview';
  return TABS.includes(tab) ? tab : 'overview';
}

/**
 * The text of a tab count: '' when unknown, the number, or "100+" at the loading cap.
 * @param {number|null|undefined} n
 * @param {number} [cap]
 */
export function tabCount(n, cap = Infinity) {
  if (!Number.isFinite(n) || n < 0) return null;
  return n >= cap ? `${cap}+` : n;
}

/**
 * The agent's latest tasks for the overview: newest first (createdAt, then updatedAt).
 * @param {Iterable<object>} tasks
 * @param {string} agentId
 * @param {number} [limit]
 */
export function latestTasks(tasks, agentId, limit = 3) {
  const time = (tk) => {
    for (const v of [tk.createdAt, tk.updatedAt, tk._at]) {
      const n = typeof v === 'number' ? v : Date.parse(v);
      if (Number.isFinite(n)) return n;
    }
    return 0;
  };
  return agentTasks(tasks, agentId).sort((a, b) => time(b) - time(a)).slice(0, Math.max(0, limit));
}

/**
 * Short French description of what an agent is doing ("utilise Gmail"; a connector of the
 * office's library by its exact key only — "utilise un connecteur du bureau" once it is no longer
 * listed).
 * @param {string|null} activity tool / connector name from the live status
 * @param {Array<{key: string, name?: string}>} [connectors]
 */
export function activityText(activity, connectors = []) {
  const a = String(activity ?? '').trim();
  if (!a) return '';
  const base = a.replace(/\(.*$/, '');
  if (/^(WebSearch|web_search)$/i.test(base)) return t('ad.activity.web');
  if (/^WebFetch$/i.test(base)) return t('ad.activity.fetch');
  if (/^(Write|Edit|Read|Glob|file_change)$/.test(base)) return t('ad.activity.files');
  // Délégation: the server says « en attente de IMAGIER » while task_wait holds (SPEC-EQUIPE §1).
  const waiting = /^en attente de (.+)$/.exec(a);
  if (waiting) return t('ad.activity.waiting', { name: waiting[1].slice(0, 40) });
  // A library connector by its exact key (its label may be a detected connector's name).
  if (isLibraryConnector(a) && !matchConnector(a, connectors)) return t('ad.activity.library');
  const key = matchConnector(a, connectors);
  const known = key ? connectors.find((c) => c.key === key) : null;
  let label = known ? connectorLabel(known.name || known.key) : '';
  if (!label) {
    const m = /^mcp__(.+?)(?:__|$)/.exec(a);
    label = connectorLabel((m ? m[1] : a).replace(/^claude_ai_/i, '').replace(/_/g, ' '));
  }
  if (label.length > 40) label = `${label.slice(0, 39)}…`;
  return t('ad.activity', { tool: label });
}

/**
 * The live status line of an agent.
 * @param {{status?: string, taskId?: string|null, activity?: string|null}|string|null} st
 * @param {Array<object>} [connectors]
 * @returns {{status: string, label: string, activity: string, taskId: string|null}}
 */
export function statusInfo(st, connectors = []) {
  const s = typeof st === 'string' ? { status: st } : (st || {});
  const status = STATUSES.includes(s.status) ? s.status : 'idle';
  const activity = status === 'working' ? activityText(s.activity, connectors) : '';
  return { status, label: t(`agentStatus.${status}`), activity, taskId: s.taskId || null };
}

/**
 * The editable settings of an agent as a flat form state.
 * @param {object} agent
 */
export function formFromAgent(agent = {}) {
  const connectors = {};
  const tools = {}; // server → the tools chosen for it (absent: every tool of the server)
  for (const c of Array.isArray(agent.connectors) ? agent.connectors : []) {
    if (!c || typeof c.server !== 'string' || !c.server) continue;
    connectors[c.server] = c.access === 'write' ? 'write' : 'read';
    if (Array.isArray(c.tools)) tools[c.server] = [...new Set(c.tools.filter((x) => typeof x === 'string' && x))];
  }
  return {
    name: normalizeName(agent.name),
    role: String(agent.role ?? ''),
    does: String(agent.does ?? ''),
    brief: Array.isArray(agent.brief) ? agent.brief.join('\n') : String(agent.brief ?? ''),
    engine: ENGINES.includes(agent.engine) ? agent.engine : 'claude',
    model: String(agent.model ?? ''),
    effort: EFFORTS.includes(agent.effort) ? agent.effort : '',
    autonomy: agent.autonomy === 'auto' ? 'auto' : 'approve',
    timeoutMin: Number.isInteger(agent.timeoutMin) ? String(agent.timeoutMin) : '',
    web: !!agent.web,
    files: !!agent.files,
    connectors,
    tools,
  };
}

/** The tool selection of a granted server as a sorted list, or null for "every tool". */
function pickOf(tools, server) {
  const list = tools?.[server];
  return Array.isArray(list) ? [...list].sort() : null;
}

function sameConnectors(a, b, ta = {}, tb = {}) {
  const ka = Object.keys(a).filter((k) => a[k] === 'read' || a[k] === 'write');
  const kb = Object.keys(b).filter((k) => b[k] === 'read' || b[k] === 'write');
  if (ka.length !== kb.length) return false;
  return ka.every((k) => a[k] === b[k] && JSON.stringify(pickOf(ta, k)) === JSON.stringify(pickOf(tb, k)));
}

/**
 * The PATCH body for the fields that changed (texts compared trimmed; the name upper-cased).
 * Connectors are sent whole, as [{server, access, tools?}], in their original order then new ones
 * (`tools` only for a server with a tool selection: absent = every tool); when `allowed` (the
 * floor's allowlist) is given, servers outside it are dropped from that list.
 * @param {ReturnType<typeof formFromAgent>} original
 * @param {ReturnType<typeof formFromAgent>} form
 * @param {{allowed?: string[]|null}} [opts]
 * @returns {object} an empty object when nothing changed
 */
export function diffAgentForm(original, form, { allowed = null } = {}) {
  const patch = {};
  const name = normalizeName(form.name);
  if (name !== normalizeName(original.name)) patch.name = name;
  for (const key of ['role', 'does', 'brief', 'model']) {
    const v = String(form[key] ?? '').trim();
    if (v !== String(original[key] ?? '').trim()) patch[key] = key === 'role' ? v.replace(/\s+/g, ' ') : v;
  }
  for (const key of ['engine', 'effort', 'autonomy']) if (form[key] !== original[key]) patch[key] = form[key];
  for (const key of ['web', 'files']) if (!!form[key] !== !!original[key]) patch[key] = !!form[key];
  const timeout = String(form.timeoutMin ?? '').trim();
  if (timeout !== String(original.timeoutMin ?? '').trim()) patch.timeoutMin = timeout ? Number(timeout) : null;
  if (!sameConnectors(original.connectors || {}, form.connectors || {}, original.tools, form.tools)) {
    const order = [...Object.keys(original.connectors || {}), ...Object.keys(form.connectors || {}).sort()];
    const seen = new Set();
    patch.connectors = [];
    for (const server of order) {
      if (seen.has(server)) continue;
      seen.add(server);
      const access = form.connectors?.[server];
      if (Array.isArray(allowed) && !allowed.includes(server)) continue; // the server refuses the whole list otherwise
      if (access !== 'read' && access !== 'write') continue;
      const tools = form.tools?.[server];
      patch.connectors.push(Array.isArray(tools) ? { server, access, tools: [...tools] } : { server, access });
    }
  }
  return patch;
}

/**
 * Client-side checks of the settings form (the server validates again).
 * @param {ReturnType<typeof formFromAgent>} form
 * @returns {Record<string, string>} field → French message (empty when valid)
 */
export function validateAgentForm(form) {
  const errors = {};
  const name = normalizeName(form.name);
  if (!name) errors.name = t('ad.v.required');
  else if (charLength(name) > LIMITS.name) errors.name = t('ad.v.tooLong', { max: LIMITS.name });
  for (const key of ['role', 'does', 'brief']) {
    if (charLength(String(form[key] ?? '').trim()) > LIMITS[key]) errors[key] = t('ad.v.tooLong', { max: LIMITS[key] });
  }
  const timeout = String(form.timeoutMin ?? '').trim();
  if (timeout && (!/^\d{1,3}$/.test(timeout) || Number(timeout) < LIMITS.timeoutMin || Number(timeout) > LIMITS.timeoutMax)) errors.timeoutMin = t('ad.v.timeout');
  const model = String(form.model ?? '').trim();
  if (model.length > LIMITS.model) errors.model = t('ad.v.tooLong', { max: LIMITS.model });
  else if (!MODEL_RE.test(model)) errors.model = t('ad.v.model');
  // A server granted with an empty selection: the server refuses `tools: []`.
  const granted = Object.keys(form.connectors || {}).filter((k) => form.connectors[k] === 'read' || form.connectors[k] === 'write');
  if (granted.some((k) => Array.isArray(form.tools?.[k]) && !form.tools[k].length)) errors.connectors = t('ad.conn.toolsNone');
  else if (granted.some((k) => Array.isArray(form.tools?.[k]) && form.tools[k].length > MAX_GRANT_TOOLS)) {
    errors.connectors = t('ad.conn.toolsMax', { max: MAX_GRANT_TOOLS });
  }
  return errors;
}

/** Most tools one grant may name (server/office/validate.js). */
export const MAX_GRANT_TOOLS = 200;

/**
 * The tools of a server for the picker, grouped Lecture / Écriture and filtered by a search (name
 * or description, case- and accent-insensitive), each group sorted by name (pure).
 * @param {Array<{name: string, kind?: string, description?: string}>} tools GET /api/agents/:id/connectors/:server/tools
 * @param {string} [query]
 * @returns {{read: object[], write: object[]}}
 */
export function groupTools(tools, query = '') {
  const fold = (v) => String(v ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const q = fold(query).trim();
  const out = { read: [], write: [] };
  const seen = new Set();
  for (const tool of Array.isArray(tools) ? tools : []) {
    if (!tool || typeof tool.name !== 'string' || !tool.name || seen.has(tool.name)) continue;
    seen.add(tool.name);
    if (q && !fold(tool.name).includes(q) && !fold(tool.description).includes(q)) continue;
    out[tool.kind === 'write' ? 'write' : 'read'].push(tool);
  }
  for (const k of ['read', 'write']) out[k].sort((a, b) => a.name.localeCompare(b.name));
  return out;
}

/**
 * The text of a row's tool line: « Outils : tous (N) » without a selection, « k sur N outils »
 * with one (pure). `total` unknown → « Outils : tous » / « k sur k outils ».
 * @param {string[]|null|undefined} picked
 * @param {number|null} total
 */
export function toolsSummary(picked, total) {
  const n = Number.isFinite(total) && total > 0 ? total : null;
  if (!Array.isArray(picked)) return n === null ? t('ad.conn.toolsAllUnknown') : t('ad.conn.toolsAll', { n });
  const k = picked.length;
  return t('ad.conn.toolsSome', { k, n: Math.max(n ?? k, k) });
}

/**
 * The selection after the picker changed (pure): null (every tool) when every known tool is
 * ticked, else the ticked names in the order of `known`, plus chosen names the catalogue no
 * longer lists (the server keeps accepting them). Never cut: more than MAX_GRANT_TOOLS is refused
 * by validateAgentForm.
 * @param {Set<string>|string[]} ticked
 * @param {string[]} known every tool of the server (names)
 * @param {string[]|null} [previous] the selection before (for names outside `known`)
 * @returns {string[]|null}
 */
export function toolSelection(ticked, known, previous = null) {
  const on = ticked instanceof Set ? ticked : new Set(ticked || []);
  const names = [...new Set((known || []).filter((x) => typeof x === 'string' && x))];
  if (names.length && names.every((n) => on.has(n))) return null;
  const out = names.filter((n) => on.has(n));
  for (const n of previous || []) if (!names.includes(n) && on.has(n) && !out.includes(n)) out.push(n);
  return out;
}

/**
 * Checks a new lesson: single line, 1..300 characters, not already there, at most 30 lessons.
 * @param {string} text
 * @param {string[]} lessons the current ones
 * @returns {{value: string, error: string|null}}
 */
export function validateLesson(text, lessons = []) {
  const value = String(text ?? '').replace(/\s+/g, ' ').trim();
  if (!value) return { value, error: t('ad.lessons.required') };
  if (charLength(value) > LIMITS.lesson) return { value, error: t('ad.lessons.tooLong', { max: LIMITS.lesson }) };
  if ((lessons || []).length >= LIMITS.lessons) return { value, error: t('ad.lessons.tooMany', { max: LIMITS.lessons }) };
  const lower = value.toLocaleLowerCase('fr-FR');
  if ((lessons || []).some((l) => String(l).replace(/\s+/g, ' ').trim().toLocaleLowerCase('fr-FR') === lower)) {
    return { value, error: t('ad.lessons.duplicate') };
  }
  return { value, error: null };
}

/**
 * True when a server matches one of the agent's loose `suggested` names ("clickup", "gmail").
 * @param {{key: string, name?: string}} server
 * @param {string[]} suggested
 */
export function isSuggested(server, suggested) {
  if (!server || !Array.isArray(suggested)) return false;
  return suggested.some((s) => typeof s === 'string' && s.trim() && matchConnector(s, [server]) === server.key);
}

/**
 * The engines a server works with: its `engines` list when it has one (the built-in « Tables du
 * bureau » serve Claude and Codex agents alike), else its one engine ('claude' | 'codex').
 * @returns {string[]}
 */
function serverEngines(server) {
  if (Array.isArray(server?.engines) && server.engines.length) return server.engines;
  if (server?.engine === 'codex' || server?.engine === 'claude') return [server.engine];
  return [String(server?.key || '').startsWith('codex-') ? 'codex' : 'claude'];
}

/**
 * Rows of the connectors section: every server allowed on the agent's floor, then the office
 * library's (added in the office: on every floor, `everyFloor`), then any server the agent still
 * has access to that the floor no longer allows (so it can be removed).
 * @param {{floor?: object|null, connectors?: object[], access?: Record<string, string>, suggested?: string[], engine?: string}} opts
 * @param {Record<string, string[]>} [opts.tools] the tool selection of each server (absent: every tool)
 * @param {(key: string) => boolean} [opts.canUse] may the person editing use this connector
 *   (« Connecteurs autorisés », web/ui/connectorpolicy.js canUseConnector); default: yes
 * @returns {Array<{key, name, status, connected, missing, readCount, writeCount, toolCount, tools, suggested, access, allowed, engineMismatch, builtin, denied,
 *   everyFloor, addedByName}>}
 *   everyFloor: a connector of the office's library (allowed on every floor, `addedByName` who
 *   added it); builtin: a server of the office itself (the « Tables du bureau »), never probed; toolCount: its
 *   number of tools (null when unknown); tools: the agent's selection (null: every tool); denied:
 *   outside the editing person's connectors — he may keep, narrow or remove the grant, never give
 *   or widen it (the server refuses), and the agent never uses it for his requests
 */
export function connectorRows({ floor = null, connectors = [], access = {}, suggested = [], engine = 'claude', tools = {}, canUse = () => true } = {}) {
  const library = libraryKeysOf(connectors);
  const allowed = [...(Array.isArray(floor?.connectors) ? floor.connectors.filter((k) => typeof k === 'string' && k && !library.includes(k)) : []), ...library];
  const keys = [...new Set(allowed)];
  for (const k of Object.keys(access || {})) if (!keys.includes(k) && (access[k] === 'read' || access[k] === 'write')) keys.push(k);
  return keys.map((key) => {
    const server = connectors.find((c) => c.key === key) || null;
    const everyFloor = server?.library === true;
    const status = server?.status || 'missing';
    const target = engine === 'codex' ? 'codex' : engine === 'claude' ? 'claude' : null;
    const readCount = Number.isFinite(server?.readCount) ? server.readCount : null;
    const writeCount = Number.isFinite(server?.writeCount) ? server.writeCount : null;
    const toolCount = Number.isFinite(server?.toolCount) ? server.toolCount
      : readCount !== null || writeCount !== null ? (readCount ?? 0) + (writeCount ?? 0) : null;
    return {
      key,
      name: everyFloor ? String(server.label || server.name || key) : connectorLabel(server?.name || key),
      server: everyFloor ? { ...server, key: server.catalogId || key, name: String(server.label || server.name || key) } : server || { key, name: key },
      status,
      connected: status === 'connected',
      missing: !server,
      readCount,
      writeCount,
      toolCount,
      tools: Array.isArray(tools?.[key]) ? tools[key] : null,
      suggested: isSuggested(server || { key, name: key }, suggested),
      access: access?.[key] === 'write' ? 'write' : access?.[key] === 'read' ? 'read' : 'none',
      allowed: allowed.includes(key),
      engineMismatch: !target || (!!server && !serverEngines(server).includes(target)),
      builtin: server?.builtin === true,
      denied: server?.builtin !== true && canUse(key) === false,
      everyFloor,
      addedByName: everyFloor && typeof server.addedByName === 'string' ? server.addedByName : '',
    };
  });
}

/**
 * The keys of the office library's connectors among `connectors` (store / GET /api/connectors):
 * granted on any floor, without the floor's allowlist.
 * @param {object[]} connectors
 * @returns {string[]}
 */
export function libraryKeysOf(connectors) {
  return (Array.isArray(connectors) ? connectors : []).filter((c) => c?.library === true && typeof c.key === 'string' && c.key).map((c) => c.key);
}

const FIELD_KEYS = ['name', 'role', 'does', 'brief', 'engine', 'model', 'effort', 'autonomy', 'timeoutMin', 'connectors', 'web', 'files', 'lessons'];
const FIELD_PREFIXES = [
  [/^nom\b/i, 'name'], [/^r[ôo]le\b/i, 'role'], [/^ce que fait/i, 'does'], [/^consignes/i, 'brief'],
  [/^dur[ée]e maximale/i, 'timeoutMin'], [/^moteur\b/i, 'engine'], [/^mod[èe]le\b/i, 'model'], [/^effort\b/i, 'effort'], [/^autonomie\b/i, 'autonomy'],
  [/^connecteur/i, 'connectors'], [/^recherche web\b/i, 'web'], [/^fichiers\b/i, 'files'], [/^r[èe]gles? apprises?\b/i, 'lessons'],
];

function fieldOfMessage(msg) {
  const m = /^([a-z]+)\s*:/.exec(msg);
  if (m && FIELD_KEYS.includes(m[1])) return m[1];
  for (const [re, field] of FIELD_PREFIXES) if (re.test(msg)) return field;
  return null;
}

/**
 * Maps the `details` of a 400 answer to form fields. Accepts arrays of French sentences
 * ("Rôle : 80 caractères au plus"), arrays of {field, message}, {problems: […]} and {field: message}.
 * @param {unknown} details
 * @param {string} [fallback] the error message, used when nothing else is readable
 * @returns {{fields: Record<string, string>, general: string[]}}
 */
export function problemsFromDetails(details, fallback = '') {
  const fields = {};
  const general = [];
  const addMsg = (field, msg) => {
    const text = String(msg ?? '').trim();
    if (!text) return;
    const f = field && FIELD_KEYS.includes(field) ? field : fieldOfMessage(text);
    if (f && f !== 'lessons') { if (!fields[f]) fields[f] = text; } else general.push(text);
  };
  const walk = (d, depth = 0) => {
    if (d == null || depth > 3) return;
    if (typeof d === 'string') { addMsg(null, d); return; }
    if (Array.isArray(d)) { for (const x of d) walk(x, depth + 1); return; }
    if (typeof d !== 'object') return;
    if (Array.isArray(d.problems)) { walk(d.problems, depth + 1); return; }
    if (typeof d.message === 'string' || typeof d.error === 'string') {
      addMsg(d.field || d.path || d.key || null, d.message || d.error);
      return;
    }
    for (const [k, v] of Object.entries(d)) {
      if (typeof v === 'string') addMsg(FIELD_KEYS.includes(k) ? k : null, v);
      else if (Array.isArray(v) && v.every((x) => typeof x === 'string')) for (const x of v) addMsg(FIELD_KEYS.includes(k) ? k : null, x);
    }
  };
  walk(details);
  if (!Object.keys(fields).length && !general.length && fallback) general.push(String(fallback));
  return { fields, general };
}

/** Images a chat message may carry (the server keeps 2 per message). */
const MAX_MESSAGE_FILES = 2;

/**
 * The images of a chat message from the API: [{name, size, mime}] (names only from the server).
 * @param {unknown} list
 */
export function chatMessageFiles(list) {
  const out = [];
  for (const f of Array.isArray(list) ? list : []) {
    if (!f || typeof f !== 'object' || typeof f.name !== 'string' || !f.name || f.name.includes('/') || f.name.includes('\\')) continue;
    out.push({ name: f.name, size: Number.isFinite(f.size) && f.size > 0 ? f.size : 0, mime: typeof f.mime === 'string' ? f.mime : '' });
    if (out.length >= MAX_MESSAGE_FILES) break;
  }
  return out;
}

/**
 * Where the browser reads an image joined to a message of the user's conversation with an agent.
 * @param {string} agentId
 * @param {string} name stored name
 */
export function chatFileUrl(agentId, name) {
  return `/api/agents/${encodeURIComponent(agentId)}/chat/files/${encodeURIComponent(name)}`;
}

/**
 * A chat message from the API in a common shape (null when unreadable). A message of the user
 * keeps the images joined to it (`files`); its text may then be empty.
 * @param {unknown} m
 * @param {'user'|'agent'} [defaultRole]
 * @returns {{role: 'user'|'agent', text: string, at: number|string|null, taskId?: string,
 *   files?: {name: string, size: number, mime: string}[]}|null}
 */
export function normalizeChatMessage(m, defaultRole = 'agent') {
  if (typeof m === 'string') return m ? { role: defaultRole, text: m, at: null } : null;
  if (!m || typeof m !== 'object') return null;
  const text = typeof m.text === 'string' ? m.text : typeof m.message === 'string' ? m.message : typeof m.content === 'string' ? m.content : null;
  if (text === null) return null;
  const role = m.role === 'user' || m.role === 'agent' ? m.role : m.role === 'assistant' ? 'agent' : defaultRole;
  const out = { role, text, at: m.at ?? m.createdAt ?? null };
  if (typeof m.taskId === 'string' && m.taskId) out.taskId = m.taskId;
  const files = role === 'user' ? chatMessageFiles(m.files) : [];
  if (files.length) out.files = files;
  return out;
}

/**
 * The history from GET /api/agents/:id/chat (an array, or {messages} / {history}).
 * @param {unknown} res
 */
export function normalizeChatHistory(res) {
  const list = Array.isArray(res) ? res : Array.isArray(res?.messages) ? res.messages : Array.isArray(res?.history) ? res.history : [];
  return list.map((m) => normalizeChatMessage(m, 'agent')).filter(Boolean);
}

/**
 * Reads the answer of POST /api/agents/:id/chat ({reply, message}; either may be a string or a
 * message object; a full `messages` history is also accepted).
 * @param {unknown} res
 * @param {string} sentText what the user sent
 * @param {number} [now]
 * @returns {{user: object, agent: object|null, history: object[]|null}}
 */
export function normalizeChatResponse(res, sentText, now = Date.now()) {
  const fallbackUser = { role: 'user', text: sentText, at: now };
  if (res && typeof res === 'object' && (Array.isArray(res.messages) || Array.isArray(res.history))) {
    const history = normalizeChatHistory(res);
    if (history.length) return { user: fallbackUser, agent: [...history].reverse().find((m) => m.role === 'agent') || null, history };
  }
  let user = fallbackUser;
  let agent = null;
  const reply = normalizeChatMessage(res?.reply, 'agent');
  const message = normalizeChatMessage(res?.message, 'agent');
  if (reply) agent = { ...reply, role: 'agent', at: reply.at ?? now };
  if (message) {
    if (message.role === 'user' || (typeof res?.message === 'string' && agent)) user = { ...message, role: 'user', at: message.at ?? now };
    else if (!agent) agent = { ...message, role: 'agent', at: message.at ?? now };
  }
  if (!agent && typeof res === 'string' && res) agent = { role: 'agent', text: res, at: now };
  return { user, agent, history: null };
}

/**
 * True when this user may create an autonomous task for the agent (SPEC §8: operators only when
 * the agent itself is autonomous).
 * @param {{canManage?: boolean, agent?: object}} opts
 */
export function canChooseAuto({ canManage = false, agent = null } = {}) {
  return !!canManage || agent?.autonomy === 'auto';
}

/**
 * Body of POST /api/tasks created from a chat message. The images go with it: those of the
 * conversation by their stored names (chatFiles), those still in the composer as [{name, data}]
 * (attachments).
 * @param {{agent: object, text: string, approve: boolean, chatFiles?: string[],
 *   attachments?: {name: string, data: string}[]}} opts
 */
export function chatTaskBody({ agent, text, approve, chatFiles = [], attachments = [] }) {
  const body = {
    floorId: agent.floorId, deptId: agent.deptId, agentId: agent.id,
    input: String(text ?? '').trim(), from: 'chat', source: { kind: 'chat' }, autonomy: approve ? 'approve' : 'auto',
  };
  const names = (Array.isArray(chatFiles) ? chatFiles : []).filter((n) => typeof n === 'string' && n);
  if (names.length) body.chatFiles = [...new Set(names)];
  if (Array.isArray(attachments) && attachments.length) body.attachments = attachments;
  return body;
}

/**
 * The images of the conversation that go with a task made from message `index` (pure): those of
 * that message when it is the user's, else those of the user's message it answers (the one
 * taskTextFromChat quotes).
 * @param {Array<{role: string, files?: {name: string}[]}>} messages
 * @param {number} index
 * @returns {{name: string, size: number, mime: string}[]}
 */
export function chatFilesForTask(messages, index) {
  const m = messages?.[index];
  if (!m) return [];
  if (m.role === 'user') return chatMessageFiles(m.files);
  for (let i = index - 1; i >= 0; i -= 1) if (messages[i]?.role === 'user') return chatMessageFiles(messages[i].files);
  return [];
}

/**
 * Index of the message that offers "Transformer en tâche" as the user's own request: the last
 * user message (−1 when there is none).
 * @param {Array<{role: string}>} messages
 */
export function lastUserIndex(messages) {
  for (let i = (messages || []).length - 1; i >= 0; i -= 1) if (messages[i]?.role === 'user') return i;
  return -1;
}

/**
 * The agents list with one agent replaced by the server's copy (a new array; the rest untouched).
 * @param {object[]} agents
 * @param {object} updated
 */
export function mergeAgent(agents, updated) {
  if (!updated || typeof updated.id !== 'string') return agents;
  let found = false;
  const out = (agents || []).map((a) => {
    if (a.id !== updated.id) return a;
    found = true;
    return { ...a, ...updated };
  });
  return found ? out : [...out, updated];
}

/**
 * The text proposed when turning a chat message into a task: the message itself, and for an
 * agent message the user's question just before it as context.
 * @param {Array<{role: string, text: string}>} messages
 * @param {number} index
 */
export function taskTextFromChat(messages, index) {
  const m = messages?.[index];
  if (!m) return '';
  const text = String(m.text ?? '').trim();
  if (m.role === 'user') return text;
  for (let i = index - 1; i >= 0; i -= 1) {
    if (messages[i]?.role === 'user') {
      const q = String(messages[i].text ?? '').trim();
      return q ? t('ad.toTask.fromAgent', { question: q, answer: text }) : text;
    }
  }
  return text;
}

/**
 * Tasks of one agent from a list (summaries), filtered by a chip filter and sorted for display.
 * @param {Iterable<object>} tasks
 * @param {string} agentId
 * @param {(task: object) => boolean} [pred]
 */
export function agentTasks(tasks, agentId, pred = () => true) {
  const out = [];
  for (const tk of tasks || []) if (tk && tk.agentId === agentId && pred(tk)) out.push(tk);
  return out;
}
